import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarGroupLabel
} from "@/components/ui/sidebar";

import { Server, LayoutDashboard, Settings, Shield, ExternalLink, Receipt, MessageSquare, BadgeCheck, BookMarked, FileSignature } from "lucide-react";
import { useChat } from "@/context/ChatProvider";
import { useGlobalAdminStatus } from "@/hooks/useGlobalAdminStatus";
import { SidebarFooterMenu } from "./SidebarFooterMenu";
import { SidebarMenuLogo } from "./SidebarMenuLogo";
import { Link } from "react-router-dom";
import { OrgSwitcher } from "@/components/organizations/org-switcher";
import ProfileSelector from "@/components/groups/ProfileSelector";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { User } from "@/context/AuthContext";
import { useAutomationServersQuery } from "@/hooks/useAutomationServersQuery";
import { useAdvisorPractices } from "@/hooks/useAdvisorPractices";
import { useOrgs } from "@/context/OrgsProvider";
import { useOrgRoles } from "@/hooks/useOrgRoles";

type AppSidebarProps = {
  user: User;
  path?: string;
};

export function AppSidebar(props: AppSidebarProps) {
  const { user, path } = props;
  const { isAdmin } = useAdminStatus();
  const { isGlobalAdmin } = useGlobalAdminStatus();
  const { activeOrg } = useOrgs();
  const { isAdvisor, practices } = useAdvisorPractices();
  const inOwnPractice = practices.some((practice) => practice.organization_id === activeOrg?.id);
  const { has } = useOrgRoles();
  const { openChat, unread: chatUnread } = useChat();
  const { data: automationServers, isLoading } = useAutomationServersQuery();
  const automationServerCount = automationServers?.pages[0]?.count ?? 0;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuLogo />
          </SidebarMenuItem>
          <SidebarSeparator />
          <SidebarMenuItem key={"orgs"}>
            <OrgSwitcher />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem key={"dashboard"}>
                <SidebarMenuButton asChild isActive={path === "/dashboard"}>
                  <Link to={"/dashboard"}>
                    <LayoutDashboard />
                    <span>{"Dashboard"}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              { isAdmin && (
              <SidebarMenuItem key={"automation-servers"}>
                <SidebarMenuButton asChild isActive={path === "/automation-servers"}>
                  <Link to={"/automation-servers"}>
                    <Server />
                    <span>{"Servers"}</span>
                    <span className="ml-auto text-xs">
                      {!isLoading && automationServerCount}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}
              <SidebarMenuItem key={"advisor-chat"}>
                <SidebarMenuButton onClick={() => openChat()} data-testid="nav-advisor-chat">
                  <MessageSquare />
                  <span>{inOwnPractice ? "Talk to client" : "Your advisor"}</span>
                  {chatUnread > 0 && (
                    <span
                      className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white"
                      data-testid="advisor-chat-unread"
                    >
                      {chatUnread}
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
              {(isAdmin || has("billing")) && (
                <SidebarMenuItem key={"contracts"}>
                  <SidebarMenuButton asChild isActive={path === "/contracts"}>
                    <Link to={"/contracts"} data-testid="nav-contracts">
                      <FileSignature />
                      <span>{"Contracts"}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {(isAdmin || has("billing")) && (
                <SidebarMenuItem key={"billing"}>
                  <SidebarMenuButton asChild isActive={path === "/billing"}>
                    <Link to={"/billing"}>
                      <Receipt />
                      <span>{"Billing"}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAdmin && (
                <SidebarMenuItem key={"settings"}>
                  <SidebarMenuButton asChild isActive={path === "/settings"}>
                    <Link to={"/settings"}>
                      <Settings />
                      <span>{"Settings"}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {isAdvisor && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>ADVISOR</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem key={"advisor-portal"}>
                    <SidebarMenuButton asChild>
                      <Link to="/advisor" data-testid="nav-advisor-portal">
                        <BadgeCheck />
                        <span>Advisor portal</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem key={"handbook"}>
                <SidebarMenuButton asChild>
                  <a href="/handbooks/company.html" target="_blank" rel="noreferrer" data-testid="nav-handbook">
                    <BookMarked />
                    <span>{"Handbook"}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {isGlobalAdmin && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>ADMIN</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem key={"global-admin-portal"}>
                    <SidebarMenuButton asChild>
                      <a
                        href={`https://${window.location.hostname.replace(/^aoc\./, 'global-admin.')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Shield />
                        <span>Global Admin Portal</span>
                        <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem key={"profiles"}>
            <ProfileSelector />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarFooterMenu
              name={user?.name}
              email={user?.email}
              avatar={user?.avatar}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}